import React, { useRef, useEffect } from 'react';
import './PixelPortrait.css';
import portrait from '../../images/personal-image.png';

const SIZE = 320;
const GRID = 5;
const DOT_RADIUS = 1.6;
const HOVER_RADIUS = 55;
const SPRING = 0.06;
const FRICTION = 0.82;
const PUSH_STRENGTH = 1.8;
const LUM_SKIP = 180;

const PixelPortrait = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, inside: false });
  const dotsRef = useRef([]);
  const animRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = SIZE;
    canvas.height = SIZE;

    const img = new Image();
    img.src = portrait;

    img.onload = () => {
      const off = document.createElement('canvas');
      off.width = SIZE;
      off.height = SIZE;
      const offCtx = off.getContext('2d');

      const ratio = Math.min(SIZE / img.width, SIZE / img.height);
      const w = img.width * ratio;
      const h = img.height * ratio;
      const dx = (SIZE - w) / 2;
      const dy = (SIZE - h) / 2;
      offCtx.drawImage(img, dx, dy, w, h);

      const data = offCtx.getImageData(0, 0, SIZE, SIZE).data;
      const dots = [];

      for (let y = 0; y < SIZE; y += GRID) {
        for (let x = 0; x < SIZE; x += GRID) {
          const idx = (y * SIZE + x) * 4;
          const a = data[idx + 3];
          if (a < 64) continue;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          const lum = 0.299 * r + 0.587 * g + 0.114 * b;
          if (lum > LUM_SKIP) continue;
          dots.push({
            ox: x,
            oy: y,
            x,
            y,
            vx: 0,
            vy: 0,
            alpha: Math.min(1, (LUM_SKIP - lum) / 130 + 0.25),
          });
        }
      }
      dotsRef.current = dots;
    };

    const handleMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      mouseRef.current.x = (e.clientX - rect.left) * scaleX;
      mouseRef.current.y = (e.clientY - rect.top) * scaleY;
      mouseRef.current.inside = true;
    };

    const handleLeave = () => {
      mouseRef.current.inside = false;
      mouseRef.current.x = -9999;
      mouseRef.current.y = -9999;
    };

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseleave', handleLeave);
    canvas.addEventListener('touchmove', (e) => {
      if (e.touches[0]) {
        handleMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
      }
    }, { passive: true });
    canvas.addEventListener('touchend', handleLeave);

    const render = () => {
      ctx.clearRect(0, 0, SIZE, SIZE);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const dots = dotsRef.current;

      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const dx = d.x - mx;
        const dy = d.y - my;
        const distSq = dx * dx + dy * dy;
        const hoverSq = HOVER_RADIUS * HOVER_RADIUS;

        if (distSq < hoverSq && distSq > 0) {
          const dist = Math.sqrt(distSq);
          const force = ((HOVER_RADIUS - dist) / HOVER_RADIUS) * PUSH_STRENGTH;
          d.vx += (dx / dist) * force;
          d.vy += (dy / dist) * force;
        }

        d.vx += (d.ox - d.x) * SPRING;
        d.vy += (d.oy - d.y) * SPRING;
        d.vx *= FRICTION;
        d.vy *= FRICTION;
        d.x += d.vx;
        d.y += d.vy;

        ctx.fillStyle = `rgba(100, 255, 218, ${d.alpha})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(render);
    };
    animRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animRef.current);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pixel-portrait"
      aria-label="Retrato pixelado interactivo"
    />
  );
};

export default PixelPortrait;
